import React, { useCallback, useMemo, useState } from 'react'
import { Virtuoso, type Components } from 'react-virtuoso'
import styled, { createGlobalStyle, css } from 'styled-components'

import { Array, AsyncStateN } from '@blade/std-lib'
import { mediaMaxTablet } from '@blade/ui-theme'
import { useTranslation } from '@root/shared/localization'

import { SearchLoupeIcon } from '../icons'
import { InfoMessage } from '../info-message'
import { hiddenScrollbarCss } from '../utils/hidden-scrollbar'

import type { IconProps } from '../icons'
import type { ChildrenProps, ClassNameProps } from '@root/shared/react'

type VirtualListProps<T> = {
  state: AsyncStateN.AsyncStateN<unknown, Array<T>>
  getKey: (it: T) => string
  renderItem: (it: T, index: number) => React.ReactElement
  renderSkeleton: (index: number) => React.ReactElement
  skeletonsCount?: number
  emptyText?: React.ReactNode
  emptyTitle?: React.ReactNode
  emptyIcon?: React.ReactElement<IconProps>
  withFade?: boolean
  onRetry?: () => void
  hasMore?: boolean
  onLoadMore?: () => void
  loadMoreState?: AsyncStateN.AsyncStateN<unknown, unknown>
} & ClassNameProps &
  ChildrenProps

export const VirtualList = <T,>({
  renderItem,
  state,
  className,
  getKey,
  renderSkeleton,
  skeletonsCount = 10,
  emptyText,
  emptyTitle,
  onRetry,
  onLoadMore,
  loadMoreState,
  hasMore,
  emptyIcon,
  withFade = true,
}: VirtualListProps<T>) => {
  const { t } = useTranslation()

  const [atTop, setAtTop] = useState(true)
  const [atBottom, setAtBottom] = useState(false)

  const skeletons = useMemo(
    () => Array.range(1, skeletonsCount),
    [skeletonsCount],
  )

  const handleEndReached = useCallback(() => {
    if (!onLoadMore || !hasMore) {
      return
    }
    if (
      loadMoreState &&
      (AsyncStateN.isLoading(loadMoreState) ||
        AsyncStateN.isError(loadMoreState))
    ) {
      return
    }
    onLoadMore()
  }, [onLoadMore, hasMore, loadMoreState])

  const Footer = useCallback(() => {
    if (!loadMoreState) {
      return undefined
    }
    if (AsyncStateN.isError(loadMoreState)) {
      return (
        <InfoContainer>
          <InfoMessage intent="error" onRetry={onLoadMore} />
        </InfoContainer>
      )
    }
    if (hasMore) {
      return (
        <LoadMoreSkeletons>
          <Item>{renderSkeleton(0)}</Item>
          <Item>{renderSkeleton(1)}</Item>
        </LoadMoreSkeletons>
      )
    }
    return undefined
  }, [loadMoreState, hasMore, onLoadMore, renderSkeleton])

  const components = useMemo((): Components<T> => {
    // Header/Footer carry the list's top/bottom padding — padding on the
    // item-list element is overridden by Virtuoso's inline scroll-offset
    // padding. Swap the padding Footer for the load-more one only when it has
    // content to render.
    const showLoadMoreFooter =
      !!loadMoreState && (AsyncStateN.isError(loadMoreState) || !!hasMore)
    return {
      List: ItemList,
      Header: HeaderPad,
      Footer: showLoadMoreFooter ? Footer : FooterPad,
    }
  }, [loadMoreState, hasMore, Footer])

  const renderEmpty = () => {
    const icon = React.cloneElement(emptyIcon ?? <SearchLoupeIcon />)

    return (
      <EmptyContainer>
        <InfoMessage
          intent="empty"
          title={emptyTitle}
          description={emptyText || t('common.try-changing-search-query')}
          icon={icon}
        />
      </EmptyContainer>
    )
  }

  return (
    <Root className={className}>
      {withFade ? <FadeProperties /> : undefined}
      {AsyncStateN.matchWaiting(state, {
        Error: () => (
          <EmptyContainer>
            <InfoMessage onRetry={onRetry} intent="error" />
          </EmptyContainer>
        ),
        Waiting: () => (
          <SkeletonList>
            {skeletons.map((it, index) => (
              <Item key={it}>{renderSkeleton(index)}</Item>
            ))}
          </SkeletonList>
        ),
        Success: (items) =>
          items.length === 0 ? (
            renderEmpty()
          ) : (
            <VirtuozoStyled
              data={items}
              computeItemKey={(_index, item: T) => getKey(item)}
              itemContent={(index, item: T) => (
                <Item>{renderItem(item, index)}</Item>
              )}
              endReached={handleEndReached}
              increaseViewportBy={300}
              components={components}
              atTopStateChange={setAtTop}
              atBottomStateChange={setAtBottom}
              data-with-fade={withFade}
              data-at-top={atTop}
              data-at-bottom={atBottom}
            />
          ),
      })}
    </Root>
  )
}

const FadeProperties = createGlobalStyle`
  @property --virtual-list-fade-start {
    syntax: '<length>';
    inherits: false;
    initial-value: 0px;
  }
  @property --virtual-list-fade-end {
    syntax: '<length>';
    inherits: false;
    initial-value: 0px;
  }
`

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const VirtuozoStyled = styled(Virtuoso<any, unknown>)`
  display: grid;
  height: 100%;
  /*
   * Promote the scroller to its own compositor layer. Without this, mouse-wheel
   * scrolling over a Virtuoso list on Windows/Chromium freezes or gets stuck
   * (trackpad and macOS are unaffected): Virtuoso mutates the DOM while
   * scrolling, and non-composited wheel scrolling runs on the (busy) main
   * thread. Known issue with the documented workaround:
   * https://github.com/petyosi/react-virtuoso/issues/675
   * react-virtuoso itself no longer applies it (verified absent in v4.18.1).
   */
  will-change: transform;

  &[data-with-fade='true'] {
    --virtual-list-fade-start: var(--virtual-list-fade-size, 12px);
    --virtual-list-fade-end: var(--virtual-list-fade-size, 12px);

    mask-image: linear-gradient(
      to bottom,
      transparent,
      black var(--virtual-list-fade-start, 12px),
      black calc(100% - var(--virtual-list-fade-end, 12px)),
      transparent
    );
    mask-size: 100%;
    mask-repeat: no-repeat;
    transition:
      --virtual-list-fade-start 0.2s ease,
      --virtual-list-fade-end 0.2s ease;

    &[data-at-top='true'] {
      --virtual-list-fade-start: 0px;
    }

    &[data-at-bottom='true'] {
      --virtual-list-fade-end: 0px;
    }
  }

  ${mediaMaxTablet(css`
    ${hiddenScrollbarCss}
  `)}
`

const Root = styled.div`
  display: grid;
  min-height: 0;
`

const EmptyContainer = styled.div`
  display: grid;
  place-content: center;
  justify-items: center;
  min-height: 0;
`

// The Virtuoso item-list element. A grid column so `row-gap` actually renders
// between items (a block list ignores it) while auto rows keep each item at its
// content height (no flex shrink). Virtuoso reads this same `row-gap` off the
// element to keep its offset math correct.
const ItemList = styled.div`
  display: grid;
  grid-auto-flow: row;
  align-content: start;
  row-gap: var(--virtual-list-gap, 10px);
  height: 100%;
`

const HeaderPad = styled.div`
  padding-block-start: var(--virtual-list-padding-block-start, 8px);
`

const FooterPad = styled.div`
  padding-block-end: var(--virtual-list-padding-block-end, 8px);
`

const Item = styled.div`
  padding-inline: var(--virtual-list-padding-inline, 12px);
`

const SkeletonList = styled.div`
  display: grid;
  gap: var(--virtual-list-gap, 10px);
  padding-block: var(--virtual-list-padding-block-start, 8px)
    var(--virtual-list-padding-block-end, 8px);
  overflow-y: auto;
  align-content: start;
  ${hiddenScrollbarCss}
`

const LoadMoreSkeletons = styled.div`
  display: grid;
  gap: var(--virtual-list-gap, 10px);
  padding-block: var(--virtual-list-gap, 10px)
    var(--virtual-list-padding-block-end, 8px);
`

const InfoContainer = styled.div`
  display: grid;
  padding-inline: var(--virtual-list-padding-inline, 12px);
`
